import { useEffect, useRef, useState } from 'react';
import { collectHeads, countByRow, type ArticleRowConfig } from '../utils/labelMatches';

interface ArticleMatches {
  counts: Map<string, number>;
  heads: HTMLElement[];
  ready: boolean;
  countedKey: string | null;
}

const EMPTY: ArticleMatches = { counts: new Map(), heads: [], ready: false, countedKey: null };

function sameMatches(a: ArticleMatches, b: ArticleMatches): boolean {
  return (
    a.ready === b.ready &&
    a.countedKey === b.countedKey &&
    a.heads.length === b.heads.length &&
    a.heads.every((h, i) => h === b.heads[i]) &&
    a.counts.size === b.counts.size &&
    Array.from(a.counts).every(([k, n]) => b.counts.get(k) === n)
  );
}

/**
 * Counts match heads per row from the rendered article DOM (R5.2). Recomputes
 * on attach and, debounced to one frame, on every DOM mutation or `recountKey`
 * change (R5.4). `countedKey` is the key current at the last recompute. State
 * is only replaced when something changed, so mark writes cannot loop.
 */
export function useArticleMatches(
  root: HTMLElement | null,
  cfg: ArticleRowConfig | null,
  recountKey: string,
): ArticleMatches {
  const [state, setState] = useState<ArticleMatches>(EMPTY);
  const keyRef = useRef(recountKey);
  keyRef.current = recountKey;
  const recomputeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!root || !cfg) {
      recomputeRef.current = null;
      setState((prev) => (sameMatches(prev, EMPTY) ? prev : EMPTY));
      return;
    }
    let raf = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const recompute = () => {
      const next: ArticleMatches = {
        counts: countByRow(root, cfg),
        heads: collectHeads(root, cfg),
        ready: root.querySelector(cfg.bodySelector) !== null,
        countedKey: keyRef.current,
      };
      setState((prev) => (sameMatches(prev, next) ? prev : next));
    };
    const cancel = () => {
      if (raf) cancelAnimationFrame(raf);
      if (timer !== undefined) clearTimeout(timer);
      raf = 0;
      timer = undefined;
    };
    const schedule = () => {
      cancel();
      const run = () => {
        cancel();
        recompute();
      };
      raf = requestAnimationFrame(run);
      timer = setTimeout(run, 16);
    };
    recomputeRef.current = schedule;
    recompute();
    const observer = new MutationObserver(schedule);
    observer.observe(root, { childList: true, subtree: true, characterData: true });
    return () => {
      observer.disconnect();
      cancel();
      recomputeRef.current = null;
    };
  }, [root, cfg]);

  useEffect(() => {
    recomputeRef.current?.();
  }, [recountKey]);

  return state;
}
